import React, { useRef, useState } from "react";
import { 
    Box, Card, CardContent, CardHeader, Typography, 
    Paper, Stack, Divider, Grid, Chip, Button, IconButton 
} from "@mui/material";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import LockIcon from '@mui/icons-material/Lock';
import CloseIcon from '@mui/icons-material/Close';
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import "react-toastify/dist/ReactToastify.css";

// Kept exactly as requested
export const SpinLoader = () => (
    <div className="spinner-border" style={{ width: "1.5rem", height: "1.5rem" }} role="status">
        <span style={{ display: 'none' }} className="sr-only">Loading...</span>
    </div>
);

const ImportCandidates = () => {
    // CHANGE THIS TO 'true' TO ENABLE THE FEATURE
    const isAvailable = false; 

    const location = useLocation();
    const [file, setFile] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const fileInputRef = useRef();

    const baseUrl = process.env.REACT_APP_API_PATH;
    const { userResult } = useSelector((state) => state.Auth);
    const vendorId = location?.state?.vendorId || userResult?.vendorid;

    // --- LOGIC (READY TO USE) ---
    const handleFileChange = (e) => {
        if (!isAvailable) return handleUnavailable();
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            const fileName = selectedFile.name;
            if (fileName.endsWith(".csv") || fileName.endsWith(".xls") || fileName.endsWith(".xlsx")) {
                setFile(selectedFile);
                setError("");
            } else {
                setFile(null);
                setError("Please select a CSV or Excel file");
                toast.error("Invalid file format.");
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isAvailable) return handleUnavailable();
        if (!file) return toast.error("Please select a file.");

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("vendorId", vendorId);
            const response = await axios.post(`${baseUrl}/ebench/import_candidate`, formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            if (response.data?.data?.statusCode === "OK") {
                toast.success("Candidates Imported Successfully.");
                setFile(null);
            } else {
                toast.error(response.data?.status?.message || "Import failed");
            }
        } catch (err) {
            toast.error("Server Error during upload.");
        } finally {
            setLoading(false);
        }
    };

    const handleUnavailable = () => {
        toast.info("This feature is currently unavailable for now.");
    };

    return (
        <Box sx={{ mt: 6, mb: 6, px: 2, display: 'flex', justifyContent: 'center' }}>
            <Card sx={{ 
                maxWidth: 600, width: '100%', borderRadius: 4, 
                boxShadow: '0 10px 30px #2d6d96', overflow: 'hidden'
            }}>
                <CardHeader 
                    title={
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            Import Team Members
                            {!isAvailable && <Chip label="Paused" size="small" sx={{ bgcolor: '#2d6d96', color: 'white' }} />}
                        </Box>
                    }
                    sx={{ 
                        background: isAvailable 
                            ? 'linear-gradient(45deg, #2d6d96 30%, #4a8eb8 90%)' 
                            : 'linear-gradient(45deg, #2d6d96 30%, #2d6d96 90%)', 
                        color: 'white', textAlign: 'center', py: 3
                    }} 
                />
                
                <CardContent sx={{ p: { xs: 3, md: 5 }, opacity: isAvailable ? 1 : 0.6 }}>
                    <Stack spacing={4}>
                        <Paper
                            variant="outlined"
                            onClick={isAvailable ? () => fileInputRef.current.click() : handleUnavailable}
                            sx={{
                                p: 5, borderStyle: 'dashed', borderWidth: 2,
                                borderColor: file ? '#2d6d96' : '#cbd5e0',
                                bgcolor: isAvailable ? (file ? '#f0f7fb' : '#fafafa') : '#f1f5f9',
                                textAlign: 'center', cursor: isAvailable ? 'pointer' : 'not-allowed',
                                position: 'relative', transition: 'all 0.3s ease'
                            }}
                        >
                            <input type="file" hidden ref={fileInputRef} onChange={handleFileChange} accept=".csv, .xls, .xlsx" />
                            
                            {!isAvailable ? (
                                <LockIcon sx={{ fontSize: 60, color: '#94a3b8', mb: 2 }} />
                            ) : (
                                <CloudUploadIcon sx={{ fontSize: 60, color: '#2d6d96', mb: 2 }} />
                            )}
                            
                            <Typography variant="h6" sx={{ color: isAvailable ? '#2d6d96' : '#64748b', fontWeight: 600 }}>
                                {!isAvailable ? "Feature Unavailable" : (file ? "File Ready" : "Select Document")}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {!isAvailable ? "This service is temporarily disabled." : (file ? file.name : "Choose CSV or Excel")}
                            </Typography>
                        </Paper>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Button
                                    fullWidth variant="outlined" startIcon={<DownloadIcon />}
                                    onClick={handleUnavailable}
                                    sx={{ height: 55, borderColor: '#cbd5e0', color: '#94a3b8', borderRadius: 2 }}
                                >
                                    Template
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Button
                                    fullWidth variant="contained"
                                    onClick={isAvailable ? handleSubmit : handleUnavailable}
                                    sx={{ 
                                        height: 55, bgcolor: isAvailable ? '#2d6d96' : '#94a3b8', 
                                        borderRadius: 2, fontWeight: 'bold',
                                        '&:hover': { bgcolor: isAvailable ? '#245a7d' : '#94a3b8' }
                                    }}
                                >
                                    {loading ? <SpinLoader /> : "Start Import"}
                                </Button>
                            </Grid>
                        </Grid>
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    );
};

export default ImportCandidates;